import { BenefitTypeDisplayTextMap } from '@strata/constants';
import { CurrentBid } from '@strata/react-current-bid';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    ColumnDefGeneric,
    FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { PatientInfoList } from '@strata/react-label-value-pairs';
import { CountdownTimer } from '@strata/react-timers';
import classNames from 'classnames';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ComponentProps, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NoLiveReferralsPage } from 'components/liveReferrals/NoLiveReferralsPage';
import { OffHoursGuard } from 'components/liveReferrals/OffHoursGuard';
import { ClosingSoonRow } from 'components/liveReferrals/offHoursScreens/ClosingSoonRow';
import { getBidState, useAuctionEnded } from 'services/AuctionHelpers';
import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    useProductConfig,
} from 'services/ProductConfigHelpers';
import { AppRoutes } from 'services/routes';
import {
    useGetLiveReferralHoursQuery,
    useGetPrescriberListsQuery,
    useGetStateListsQuery,
} from 'store/api/auctionApi';
import { useGetPlaceOfServiceMapQuery } from 'store/api/referenceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';
import { LiveReferral, useLiveReferralData } from 'store/liveReferrals/liveReferralsHooks';

dayjs.extend(utc);
dayjs.extend(timezone);

const CLOSING_SOON_ROW = 'CLOSING_SOON_ROW';

const initialSortProperties: ComponentProps<typeof GridWithFilters>['sortProperties'] = [
    { id: 'auctionEnd', desc: false },
];

/* eslint-disable react/prop-types */
const QuantityCell: ColumnDefGeneric<LiveReferral, 'qty'>['cell'] = ({ getValue, row }) => {
    const { segmentId, productServiceOid, benefit } = row.original;
    const productConfig = useProductConfig({
        segmentId,
        productServiceOid,
        benefit,
    });
    return <SecondaryData data={addQtyUnits(getValue(), getEstDispensedQtyUnits(productConfig))} />;
};
const BidAmtCell: ColumnDefGeneric<LiveReferral, 'currentBidAmt'>['cell'] = ({ getValue, row }) => {
    const { auctionEnd, currentBidState, referentPriceType } = row.original;
    const auctionEnded = useAuctionEnded(auctionEnd);
    return (
        <SecondaryData
            data={
                <CurrentBid
                    bidAmount={getValue()}
                    referentPriceType={referentPriceType}
                    bidState={getBidState(currentBidState, auctionEnded)}
                />
            }
        />
    );
};
const TimeRemainingCell: ColumnDefGeneric<LiveReferral, 'auctionEnd'>['cell'] = ({ getValue }) => {
    const auctionEnded = useAuctionEnded(getValue());
    return getValue() ? (
        <CountdownTimer
            endTime={getValue()}
            tooltipText={
                auctionEnded
                    ? 'Refresh your browser if you want to remove ended referrals.'
                    : undefined
            }
        />
    ) : null;
};
/* eslint-enable react/prop-types */

const columns: ColumnDef<LiveReferral>[] = [
    {
        accessorKey: 'product',
        header: 'Product',
        size: 180,
        cell: ({ getValue, row }) =>
            row.original.referralId === CLOSING_SOON_ROW ? (
                <ClosingSoonRow />
            ) : (
                <ProductCell product={getValue()} isBrand={row.original.isBrand} primary />
            ),
    },
    {
        accessorKey: 'strength',
        header: 'Strength',
        size: 80,
    },
    {
        accessorKey: 'qty',
        header: 'Est Qty',
        size: 80,
        cell: QuantityCell,
    },
    {
        accessorKey: 'benefit',
        header: 'Benefit',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={BenefitTypeDisplayTextMap[getValue()]} />,
    },
    {
        accessorKey: 'segmentName',
        header: 'Health Plan',
        size: 120,
    },
    {
        accessorKey: 'valueCases',
        header: 'Attributes',
        size: 90,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <PatientInfoList
                values={getValue()}
                incentiveTotal={row.original.incentiveTotal}
                iconsOnly
            />
        ),
    },
    {
        accessorKey: 'currentBidAmt',
        header: 'Current Winning Bid',
        size: 100,
        enableSorting: false,
        cell: BidAmtCell,
    },
    {
        accessorKey: 'auctionEnd',
        header: 'Time Remaining',
        size: 100,
        cell: TimeRemainingCell,
    },
];

export const LiveReferralsListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();
    const { liveReferralData, liveReferralDataLoaded } = useLiveReferralData();

    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();

    const { data: prescriberListData } = useGetPrescriberListsQuery();
    const { data: stateListData } = useGetStateListsQuery();

    // Property name and display text for filter categories
    const filterCategories: FilterCategory<LiveReferral>[] = [
        { key: 'segmentName', title: 'Health Plan' },
        { key: 'product', title: 'Product' },
        {
            key: 'isBrand',
            title: 'Product Type',
            customValues: [
                {
                    value: 'Brand',
                    handler: (isBrand) => isBrand,
                },
                {
                    value: 'Generic',
                    handler: (isBrand) => !isBrand,
                },
            ],
        },

        {
            key: 'benefit',
            title: 'Benefit',
            valueDisplayTextMap: BenefitTypeDisplayTextMap,
        },
        { key: 'diagnosisName', title: 'Diagnosis' },
        {
            key: 'placeOfService',
            title: 'Place Of Service',
            valueDisplayTextMap: placeOfServiceMap,
        },
        ...((prescriberListData ?? []).length > 0
            ? [
                  {
                      key: 'prescriberId',
                      title: 'Prescriber List',
                      customValues: prescriberListData!.map((list) => ({
                          value: list.listName,
                          handler: (prescriberId) =>
                              list.prescribers?.some(({ npi }) => npi === prescriberId),
                      })),
                  } as FilterCategory<LiveReferral>,
              ]
            : []),
        ...((stateListData ?? []).length > 0
            ? [
                  {
                      key: 'memberState',
                      title: 'State List',
                      customValues: stateListData!.map((list) => ({
                          value: list.listName,
                          handler: (memberState) => list.states?.includes(memberState),
                      })),
                  } as FilterCategory<LiveReferral>,
              ]
            : []),
        {
            key: 'currentBidState',
            title: 'Current Bid State',
            valueDisplayTextMap: {
                [CurrentBid.states.LOSING]: 'Losing',
                [CurrentBid.states.NO_BID]: 'Not Bid On',
                [CurrentBid.states.WINNING]: 'Winning',
            },
        },
    ];

    const { data: liveReferralHours } = useGetLiveReferralHoursQuery();
    const [closingSoon, setClosingSoon] = useState(false);
    useEffect(() => {
        const interval = setInterval(() => {
            setClosingSoon(
                dayjs().tz('America/New_York').hour() >= (liveReferralHours?.lastStart ?? 0),
            );
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [liveReferralHours]);

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.LIVE_REFERRALS, initialSortProperties);

    return (
        <OffHoursGuard>
            {liveReferralDataLoaded && liveReferralData.length === 0 ? (
                <NoLiveReferralsPage />
            ) : (
                <FormPageContainer>
                    <GridWithFilters
                        title="Live Referrals"
                        secondaryTitle={loggedInSupplierName}
                        data={[
                            ...liveReferralData,
                            ...(closingSoon
                                ? [
                                      // Displays the Closing Soon row pinned to the bottom of the grid
                                      {
                                          referralId: CLOSING_SOON_ROW,
                                          pinBottom: true,
                                      } as unknown as LiveReferral,
                                  ]
                                : []),
                        ]}
                        isLoading={!liveReferralDataLoaded}
                        filterCategories={filterCategories}
                        activeFilters={activeFilters}
                        onFilter={updateGridActiveFilters}
                        filterPaneRenderWidth={filterPaneWidth}
                        onFilterPaneResize={updateFilterPaneWidth}
                        sortProperties={sort}
                        onSort={updateGridSort}
                        enableSearch
                        searchText={search}
                        onSearch={updateGridSearch}
                        onRowClick={(rowData) =>
                            navigate(AppRoutes.liveReferralDetails(rowData.referralId))
                        }
                        rowCssClass={(rowData) =>
                            classNames({
                                'griddle-row--closing-soon':
                                    rowData.referralId === CLOSING_SOON_ROW,
                            })
                        }
                        columns={columns}
                    />
                </FormPageContainer>
            )}
        </OffHoursGuard>
    );
};
